import type { Author, ContractedBook } from '@/data/types';

export const CALIDRIS_ID = 'calidris';
export const CALIDRIS_NAVN = 'Calidris Forlag';

export const CALIDRIS_COOLDOWN_DAGER = 5;

export const CALIDRIS_VANITY_SJANSE = 0.03;
export const CALIDRIS_SPAM_SJANSE = 0.02;
export const CALIDRIS_FAKE_REVIEW_SJANSE = 0.02;
export const CALIDRIS_LYDBOK_SJANSE = 0.02;

export const CALIDRIS_SPAM_MIN_FORFATTERE = 3;
export const CALIDRIS_FAKE_REVIEW_MIN_UTGITTE = 5;

export const CALIDRIS_VANITY_BELOEP = 30_000;
export const CALIDRIS_MATCH_FORSKUDD = 15_000;
export const CALIDRIS_KURS_KOSTNAD = 20_000;
export const CALIDRIS_KRONIKK_KOSTNAD = 5_000;
export const CALIDRIS_ADVOKAT_KOSTNAD = 25_000;

export const CALIDRIS_VANITY_DROP_SJANSE = 0.10;
export const CALIDRIS_SPAM_DROP_SJANSE = 0.05;
export const CALIDRIS_KRONIKK_KONTROVERS_SJANSE = 0.30;

export const CALIDRIS_FORHANDLET_PROSENT = 10;
export const CALIDRIS_AKSEPT_PROSENT = 15;

export const CALIDRIS_PREFIKS = 'synth_calidris_';

export type CalidrisEventVariant =
  | 'vanity'
  | 'spam'
  | 'fake_review'
  | 'lydbok_stoel'
  | 'sven_vanity';

export const CALIDRIS_VARIANT_TITLER: Record<CalidrisEventVariant, string> = {
  vanity: 'Calidris-tilbudet',
  spam: 'Calidris spam-bomber forfatterne',
  fake_review: 'Calidris plantet en falsk anmeldelse',
  lydbok_stoel: 'Calidris krever kutt i lydbok-royalty',
  sven_vanity: 'Calidris frir til deg',
};

export function byggCalidrisEventId(
  variant: CalidrisEventVariant,
  dag: number,
  nokkel: string,
): string {
  return `${CALIDRIS_PREFIKS}${variant}_${nokkel}_${dag}`;
}

export function erCalidrisEventId(eventId: string): boolean {
  return eventId.startsWith(CALIDRIS_PREFIKS);
}

export function calidrisVariantFraEventId(
  eventId: string,
): CalidrisEventVariant | null {
  if (!erCalidrisEventId(eventId)) return null;
  const rest = eventId.slice(CALIDRIS_PREFIKS.length);
  if (rest.startsWith('sven_vanity_')) return 'sven_vanity';
  if (rest.startsWith('vanity_')) return 'vanity';
  if (rest.startsWith('spam_')) return 'spam';
  if (rest.startsWith('fake_review_')) return 'fake_review';
  if (rest.startsWith('lydbok_stoel_')) return 'lydbok_stoel';
  return null;
}

export function kandidaterForVanity(
  authors: Record<string, Author>,
): Author[] {
  const ut: Author[] = [];
  for (const a of Object.values(authors)) {
    if (a.paaKurs) continue;
    ut.push(a);
  }
  return ut;
}

export function antallAktiveForfattere(
  authors: Record<string, Author>,
): number {
  let n = 0;
  for (const a of Object.values(authors)) {
    if (a.paaKurs) continue;
    n += 1;
  }
  return n;
}

export type CalidrisAvkortning = {
  isbn: string;
  prosentTilCalidris: number;
};

export function trekkCalidrisAvkortning(
  isbn: string,
  inntekt: number,
  avkortninger: ReadonlyArray<CalidrisAvkortning>,
): { tilSpiller: number; tilCalidris: number; prosent: number } {
  let prosent = 0;
  for (const a of avkortninger) {
    if (a.isbn !== isbn) continue;
    prosent += a.prosentTilCalidris;
  }
  if (prosent <= 0) return { tilSpiller: inntekt, tilCalidris: 0, prosent: 0 };
  if (prosent > 100) prosent = 100;
  const tilCalidris = Math.round((inntekt * prosent) / 100);
  return { tilSpiller: inntekt - tilCalidris, tilCalidris, prosent };
}

export function harLydbokAvtale(
  isbn: string,
  alleAvtaler: ReadonlyArray<{ isbn: string }>,
): boolean {
  for (const a of alleAvtaler) {
    if (a.isbn === isbn) return true;
  }
  return false;
}

export function finnFoersteLydbokIsbn(
  lydbokAvtaler: ReadonlyArray<{ isbn: string; bokTittel: string; tjenesteNavn: string }>,
): { isbn: string; tittel: string; tjenesteNavn: string } | null {
  if (lydbokAvtaler.length === 0) return null;
  const a = lydbokAvtaler[0];
  return { isbn: a.isbn, tittel: a.bokTittel, tjenesteNavn: a.tjenesteNavn };
}

export function antallUtgitteBoker(
  iProduksjon: ReadonlyArray<ContractedBook>,
  utgitteBoker: ReadonlyArray<ContractedBook>,
): number {
  let n = 0;
  for (const b of iProduksjon) if (b.status === 'utgitt') n += 1;
  n += utgitteBoker.length;
  return n;
}
